// Shared AI model configuration for Fal AI video generation
// This is the single source of truth - used by both generation.ts and workflowAgent.ts

export interface AIModelConfig {
  endpoint: string;
  category: "text-to-video" | "image-to-video" | "video-to-video" | "reference-to-video" | "tts" | "avatar" | "voice-create" | "transcription";
  imageParams: { first: string; last?: string; refs?: string };
  elementParams?: { key: string; frontalKey: string; referenceKey: string };
  videoParams?: { input: string; keepAudio?: string };
  audioParams?: { text?: string; refAudio?: string; refText?: string; audioUrl?: string; imageUrl?: string };
  durationFormat: "string" | "integer" | "string_s" | "none";
  durationRange?: { min: number; max: number; step: number };
  validDurations?: number[];
  supportsCfgScale?: boolean;
  supportsNegativePrompt?: boolean;
  supportsGenerateAudio?: boolean;
  audioAlwaysOn?: boolean;
  audioApiKey?: string;
  supportsVoiceIds?: boolean;
  maxVoiceIds?: number;
  supportsMultiPrompt?: boolean;
  timeoutMs?: number;
  maxPromptLength?: number;
  maxAudioDurationMs?: number;
}

export const AI_MODELS: Record<string, AIModelConfig> = {
  // Kling V3 Pro models (default for text-to-video)
  "v3-text-to-video": {
    endpoint: "fal-ai/kling-video/v3/pro/text-to-video",
    category: "text-to-video",
    imageParams: { first: "start_image_url" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsVoiceIds: true,
    maxVoiceIds: 2,
    supportsMultiPrompt: true,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  "v3-image-to-video": {
    endpoint: "fal-ai/kling-video/v3/pro/image-to-video",
    category: "image-to-video",
    imageParams: { first: "start_image_url", last: "end_image_url" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsVoiceIds: true,
    maxVoiceIds: 2,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  // Kling O3 Pro models (default for everything else)
  "o3-text-to-video": {
    endpoint: "fal-ai/kling-video/o3/pro/text-to-video",
    category: "text-to-video",
    imageParams: { first: "start_image_url" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsVoiceIds: true,
    maxVoiceIds: 2,
    supportsMultiPrompt: true,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  "o3-image-to-video": {
    endpoint: "fal-ai/kling-video/o3/pro/image-to-video",
    category: "image-to-video",
    imageParams: { first: "image_url", last: "end_image_url" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsVoiceIds: true,
    maxVoiceIds: 2,
    supportsCfgScale: true,
    supportsNegativePrompt: true,
    supportsMultiPrompt: true,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  "o3-reference-to-video": {
    endpoint: "fal-ai/kling-video/o3/pro/reference-to-video",
    category: "reference-to-video",
    imageParams: { first: "start_image_url", refs: "image_urls" },
    elementParams: { key: "elements", frontalKey: "frontal_image_url", referenceKey: "reference_image_urls" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsVoiceIds: true,
    maxVoiceIds: 2,
    supportsCfgScale: true,
    supportsNegativePrompt: true,
    supportsMultiPrompt: true,
    timeoutMs: 15 * 60 * 1000,
    maxPromptLength: 2500,
  },
  "o3-video-edit": {
    endpoint: "fal-ai/kling-video/o3/pro/video-to-video/edit",
    category: "video-to-video",
    imageParams: { first: "start_image_url" },
    videoParams: { input: "video_url", keepAudio: "keep_audio" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsCfgScale: true,
    supportsNegativePrompt: true,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  "o3-video-reference": {
    endpoint: "fal-ai/kling-video/o3/pro/video-to-video/reference",
    category: "video-to-video",
    imageParams: { first: "start_image_url" },
    videoParams: { input: "video_url" },
    durationFormat: "string",
    durationRange: { min: 3, max: 15, step: 1 },
    audioAlwaysOn: true,
    supportsCfgScale: true,
    supportsNegativePrompt: true,
    timeoutMs: 10 * 60 * 1000,
    maxPromptLength: 2500,
  },
  // Veo 3.1 models (fast tier)
  "veo-text-to-video": {
    endpoint: "fal-ai/veo3.1/fast",
    category: "text-to-video",
    imageParams: { first: "image_url" },
    durationFormat: "string_s",
    validDurations: [4, 6, 8],
    supportsGenerateAudio: true,
    supportsNegativePrompt: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 20000,
  },
  "veo-image-to-video": {
    endpoint: "fal-ai/veo3.1/fast/image-to-video",
    category: "image-to-video",
    imageParams: { first: "image_url" },
    durationFormat: "string_s",
    validDurations: [4, 6, 8],
    supportsGenerateAudio: true,
    supportsNegativePrompt: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 20000,
  },
  "veo-reference-to-video": {
    endpoint: "fal-ai/veo3.1/fast/reference-to-video",
    category: "reference-to-video",
    imageParams: { first: "image_url", refs: "image_urls" },
    durationFormat: "string_s",
    validDurations: [8],
    supportsGenerateAudio: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 20000,
  },
  "veo-first-last-frame": {
    endpoint: "fal-ai/veo3.1/fast/first-last-frame-to-video",
    category: "image-to-video",
    imageParams: { first: "first_frame_url", last: "last_frame_url" },
    durationFormat: "string_s",
    validDurations: [4, 6, 8],
    supportsGenerateAudio: true,
    supportsNegativePrompt: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 20000,
  },
  "veo-extend-video": {
    endpoint: "fal-ai/veo3.1/fast/extend-video",
    category: "video-to-video",
    imageParams: { first: "image_url" },
    videoParams: { input: "video_url" },
    durationFormat: "string_s",
    validDurations: [7],
    supportsGenerateAudio: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 20000,
  },
  // Seedance 2.0 models (ByteDance, fast tier)
  "seedance-text-to-video": {
    endpoint: "bytedance/seedance-2.0/fast/text-to-video",
    category: "text-to-video",
    imageParams: { first: "image_url" },
    durationFormat: "string",
    durationRange: { min: 4, max: 15, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 5 * 60 * 1000,
    maxPromptLength: 2000,
  },
  "seedance-image-to-video": {
    endpoint: "bytedance/seedance-2.0/fast/image-to-video",
    category: "image-to-video",
    imageParams: { first: "image_url", last: "end_image_url" },
    durationFormat: "string",
    durationRange: { min: 4, max: 15, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 5 * 60 * 1000,
    maxPromptLength: 2000,
  },
  "seedance-reference-to-video": {
    endpoint: "bytedance/seedance-2.0/fast/reference-to-video",
    category: "reference-to-video",
    imageParams: { first: "image_url", refs: "image_urls" },
    durationFormat: "string",
    durationRange: { min: 4, max: 15, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 5 * 60 * 1000,
    maxPromptLength: 2000,
  },
  // Grok Imagine models (relaxed content policy fallback)
  "grok-text-to-video": {
    endpoint: "xai/grok-imagine-video/text-to-video",
    category: "text-to-video",
    imageParams: { first: "image_url" },
    durationFormat: "integer",
    durationRange: { min: 1, max: 15, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 4096,
  },
  "grok-image-to-video": {
    endpoint: "xai/grok-imagine-video/image-to-video",
    category: "image-to-video",
    imageParams: { first: "image_url" },
    durationFormat: "integer",
    durationRange: { min: 1, max: 15, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 4096,
  },
  "grok-edit-video": {
    endpoint: "xai/grok-imagine-video/edit-video",
    category: "video-to-video",
    imageParams: { first: "image_url" },
    videoParams: { input: "video_url" },
    durationFormat: "integer",
    durationRange: { min: 1, max: 8, step: 1 },
    supportsGenerateAudio: true,
    timeoutMs: 6 * 60 * 1000,
    maxPromptLength: 4096,
  },
  // ElevenLabs TTS models (via Fal)
  "elevenlabs-tts-v3": {
    endpoint: "fal-ai/elevenlabs/tts/eleven-v3",
    category: "tts",
    imageParams: { first: "" },
    audioParams: { text: "text" },
    durationFormat: "none",
    timeoutMs: 2 * 60 * 1000,
  },
  "elevenlabs-dialogue-v3": {
    endpoint: "fal-ai/elevenlabs/text-to-dialogue/eleven-v3",
    category: "tts",
    imageParams: { first: "" },
    audioParams: { text: "inputs" },
    durationFormat: "none",
    timeoutMs: 3 * 60 * 1000,
  },
  "elevenlabs-tts-multilingual-v2": {
    endpoint: "fal-ai/elevenlabs/tts/multilingual-v2",
    category: "tts",
    imageParams: { first: "" },
    audioParams: { text: "text" },
    durationFormat: "none",
    timeoutMs: 2 * 60 * 1000,
  },
  // Veed Fabric 1.0 (avatar with native lip sync) — 30s audio hard cap, no prompt param
  "fabric-avatar": {
    endpoint: "veed/fabric-1.0",
    category: "avatar",
    imageParams: { first: "image_url" },
    audioParams: { audioUrl: "audio_url" },
    durationFormat: "none",
    timeoutMs: 10 * 60 * 1000,
    maxAudioDurationMs: 30_000,
  },
  // Creatify Aurora (high-fidelity avatar with full-body gestures)
  "aurora-avatar": {
    endpoint: "fal-ai/creatify/aurora",
    category: "avatar",
    imageParams: { first: "image_url" },
    audioParams: { audioUrl: "audio_url" },
    durationFormat: "none",
    timeoutMs: 10 * 60 * 1000,
    maxAudioDurationMs: 60_000,
    maxPromptLength: 2000,
  },
  // HeyGen Avatar 4 (expressive talking head with configurable style)
  "heygen-avatar": {
    endpoint: "fal-ai/heygen/avatar4/image-to-video",
    category: "avatar",
    imageParams: { first: "image_url" },
    audioParams: { audioUrl: "audio_url" },
    durationFormat: "none",
    timeoutMs: 10 * 60 * 1000,
    maxAudioDurationMs: 60_000,
    maxPromptLength: 2000,
  },
  // Deprecated: Kling Avatar v2 Pro (replaced by fabric-avatar)
  "kling-avatar-v2-pro": {
    endpoint: "fal-ai/kling-video/ai-avatar/v2/pro",
    category: "avatar",
    imageParams: { first: "image_url" },
    audioParams: { audioUrl: "audio_url" },
    durationFormat: "none",
    timeoutMs: 10 * 60 * 1000,
  },
  "kling-create-voice": {
    endpoint: "fal-ai/kling-video/create-voice",
    category: "voice-create",
    imageParams: { first: "" },
    durationFormat: "none",
    timeoutMs: 2 * 60 * 1000,
  },
  // Transcription (Whisper via Fal — standard whisper supports chunk_level: "word")
  "wizper": {
    endpoint: "fal-ai/whisper",
    category: "transcription",
    imageParams: { first: "" },
    durationFormat: "none",
    timeoutMs: 2 * 60 * 1000,
  },
};

// avatar_model feature flag option → AI_MODELS key
export const AVATAR_MODEL_KEYS: Record<string, string> = {
  aurora: "aurora-avatar",
  heygen: "heygen-avatar",
  fabric: "fabric-avatar",
};
